/**
 * Error reporting to Bugsink.
 *
 * The whole stack reports to one project per **environment**, not per app:
 * project 3 for staging, project 4 for production, shared with the Django
 * backend (`core/settings/{staging,production}.py`) and frontend_v3. A
 * teacher's bug report is usually "it broke" — the trace is the only thing
 * that says where.
 *
 * The DSN comes from `REACT_APP_SENTRY_DSN`, baked in at build time. Unset —
 * which is the case for local development — makes `init` a no-op, so a
 * developer's crashes stay out of the shared project.
 */
import * as Sentry from '@sentry/react'

// Mirrors `Errors.RPC_3_OBJECT_NOT_FOUND` / `RPC_5_AUTH_REQUIRED` on the
// backend. Named here rather than inlined as `3`/`5` because a renumbering
// would otherwise leave this filter dropping the wrong codes — and the
// symptom, errors quietly not reported, is invisible by construction.
// frontend_v3 imports the same pair from `lib/rpc/types/base`; teacher-admin
// has no shared module for them.
const OBJECT_NOT_FOUND = 3
const AUTH_REQUIRED = 5

export function initErrorReporting() {
  const dsn = process.env.REACT_APP_SENTRY_DSN
  if (!dsn) return

  Sentry.init({
    dsn,
    environment: process.env.REACT_APP_ENVIRONMENT || 'production',
    release: process.env.REACT_APP_VERSION,

    // Errors only. Bugsink is an error tracker, not an APM — tracing
    // envelopes are volume it has no use for.
    tracesSampleRate: 0,

    ignoreErrors: [
      // A navigation cancelling an in-flight chunk fetch, and the extension
      // and webview noise every browser generates. None of it is our code.
      'Loading chunk',
      'ChunkLoadError',
      'ResizeObserver loop',
    ],

    beforeSend(event, hint) {
      // The JSON-RPC dispatcher reports failures as `{code, message}` in the
      // response body, and the admin surfaces several of them as ordinary UI
      // states. `RPC_3` (not found) and `RPC_5` (auth required) are protocol
      // answers, not faults — the same rule the backend applies by never
      // capturing `RpcError`.
      const code = hint?.originalException?.code
      if (code === OBJECT_NOT_FOUND || code === AUTH_REQUIRED) return null
      return event
    },
  })
}
